import { inject } from "@angular/core";
import { Observable, shareReplay, Subscriber } from "rxjs";

import { AdaaHelper } from "../../core/utils";
import { Constants } from "../constants/constants";
import { AdaaBoolean } from "../constants/enums";
import {
  EntityModelType,
  GovDirection,
  GovProjectMilestone,
  IntpCountryModel,
  MainResponseModel,
  OrgUnit,
  ServiceModel,
  SubGovDirection,
  ValueText,
} from "../models";
import { AgmCycleApiService } from "./agm-cycle-api-service";
import { AuditTrailApiService } from "./audit-trail-api-service";
import { CyclesApiService } from "./cycles-api-service";
import { EntitiesApiService } from "./entities-api-service";
import { GovDirectionApiService } from "./gov-direction-api-service";
import { GovProjectsApiService } from "./gov-projects-api-service";
import { GovScoresApiService } from "./gov-scores-api-service";
import { IntpCountriesApiService } from "./intp-countries-api-service";
import { IntpProjectsApiService } from "./intp-projects-api-service";
import { NotificationsApiService } from "./notifications-api-service";
import { ObjectivesApiService } from "./objectives-api-service";
import { OrgUnitApiService } from "./org-unit-api-service";
import { OtherEntitiesApiService } from "./other-entities-api-service";
import { PillarsApiService } from "./pillars-api-service";
import { PropertiesService } from "./properties-service";
import { ServicesApiService } from "./services-api-service";
import { SubGovDirectionApiService } from "./sub-gov-direction-api-service";
import { TpApiService } from "./tp-api-service";
import { TpCyclesApiService } from "./tp-cycles-api-service";
import { UserGroupsApiService } from "./user-groups-api-service";
import { UsersApiService } from "./users-api-service";

type DataCallback<T> = (data: T) => void;

/**
 * Helper to process dropdown API requests
 */
const processDropdownRequest = <T>(
  subscriber: Subscriber<ValueText[]>,
  apiCall: Observable<MainResponseModel<T[]>>,
  valueField: string = AdaaHelper.getFieldLanguage("name"),
  textField: string = AdaaHelper.getFieldLanguage("name"),
  extraField?: string,
  cb?: DataCallback<T[]>
) => {
  apiCall.subscribe({
    next: (response) => {
      if (response.inError) return;
      if (cb) cb(response.responseData);
      subscriber.next(AdaaHelper.setDropdownArray(response.responseData, valueField, textField, extraField));
    },
    error: (err) => subscriber.error(err),
  });
};

/**
 * Helper to process dropdown API requests that list the distinct values of one field
 */
const processDistinctDropdownRequest = <T>(
  subscriber: Subscriber<ValueText[]>,
  apiCall: Observable<MainResponseModel<T[]>>,
  field: keyof T
) => {
  apiCall.subscribe({
    next: (response) => {
      if (response.inError) return;
      const values = response.responseData
        .map((item) => item[field])
        .filter((value) => AdaaHelper.isDefinedAndNotEmpty(value));
      subscriber.next(AdaaHelper.setDropdownArray([...new Set(values)], String(field), String(field)));
    },
    error: (err) => subscriber.error(err),
  });
};

/**
 * Populates the cycles dropdown-list
 */
const cyclesDropdownList = () => {
  const cyclesApiService = inject(CyclesApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    if (fieldName.includes("planName")) {
      processDropdownRequest(subscriber, cyclesApiService.getAll());
    }
  };
};

/**
 * Populates the objectives dropdown-list
 */
const objectivesDropdownList = () => {
  const objectivesApiService = inject(ObjectivesApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    if (fieldName.includes("objectiveName")) {
      processDropdownRequest(
        subscriber,
        objectivesApiService.getObjectivesByEntityAndPlan({
          entityId: Number(AdaaHelper.entity?.id),
          planId: Number(AdaaHelper.plan?.id),
        })
      );
    }
  };
};

/**
 * Populates the pillars dropdown-list
 */
const pillarsDropdownList = () => {
  const pillarApiService = inject(PillarsApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    if (fieldName.includes("pillarName")) {
      processDropdownRequest(subscriber, pillarApiService.getPillars());
    }
  };
};

/**
 * Populates the tp dropdown-list
 */
const tpDropdownList = () => {
  const tpApiService = inject(TpApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    if (fieldName === "nationalProjectNameAE" || fieldName === "nationalProjectNameEN") {
      processDropdownRequest(subscriber, tpApiService.getAllRelatedByEntity(AdaaHelper.entity.id));
    }
  };
};

/**
 * Populates the other entities dropdown-list
 */
const otherEntitiesDropdownList = () => {
  const otherEntitiesApiService = inject(OtherEntitiesApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    if (fieldName.includes("otherEntityName")) {
      processDropdownRequest(subscriber, otherEntitiesApiService.getAll());
    }
  };
};

/**
 * Populates the entities dropdown-list
 */
const entitiesDropdownList = () => {
  const entitiesApiService = inject(EntitiesApiService);

  return (
    subscriber: Subscriber<ValueText[]>,
    { fieldName, cb }: { fieldName: string; cb: DataCallback<EntityModelType[]> }
  ) => {
    const excludeExternalEntity = fieldName.includes("decidingEntityName") || fieldName.includes("userGroupEntityName");

    const shouldRun =
      fieldName.includes("entityName") ||
      fieldName.includes("sponsorEntityName") ||
      fieldName.includes("decidingEntityName") ||
      fieldName.includes("userGroupEntityName") ||
      fieldName.includes("responsibleEntityName") ||
      fieldName.includes("enablerEntityName");

    if (shouldRun) {
      entitiesApiService
        .getAll(
          excludeExternalEntity ? AdaaBoolean.N : AdaaBoolean.Y,
          excludeExternalEntity ? AdaaBoolean.N : AdaaBoolean.Y
        )
        .subscribe({
          next: (response) => {
            if (response.inError) return;

            cb(response.responseData);

            //NOTE: If the column is enabler entity only show the entities with enabler flag
            const data = fieldName.includes("enablerEntityName")
              ? response.responseData.filter((e) => e.enabler === AdaaBoolean.Y)
              : response.responseData;

            subscriber.next(
              AdaaHelper.setDropdownArray(
                data,
                AdaaHelper.getFieldLanguage("name"),
                AdaaHelper.getFieldLanguage("name")
              )
            );
          },
        });
    }
  };
};

/**
 * Populates the orgUnit dropdown-list
 */
const orgUnitDropdownList = () => {
  const orgUnitApiService = inject(OrgUnitApiService);

  return (
    subscriber: Subscriber<ValueText[]>,
    { fieldName, entityId, cb }: { fieldName: string; entityId?: number; cb: DataCallback<OrgUnit[]> }
  ) => {
    if (fieldName.includes("orgUnitName")) {
      processDropdownRequest<OrgUnit>(
        subscriber,
        orgUnitApiService.getAllByEntity(Number(entityId ?? AdaaHelper.entity?.id)),
        AdaaHelper.getFieldLanguage("name"),
        AdaaHelper.getFieldLanguage("name"),
        AdaaHelper.getFieldLanguage("parentOrgUnitName"),
        cb
      );
    }
  };
};

/**
 * Populates the subGovDirection dropdown-list
 */
const subGovDirectionDropdownList = () => {
  const subGovDirectionApiService = inject(SubGovDirectionApiService);

  return (
    subscriber: Subscriber<ValueText[]>,
    { fieldName, cb }: { fieldName: string; cb: DataCallback<SubGovDirection[]> }
  ) => {
    /*
     * All sub gov-dir
     * */
    if (fieldName.includes("subGovDirectionName")) {
      processDropdownRequest<SubGovDirection>(
        subscriber,
        subGovDirectionApiService.getAll(),
        AdaaHelper.getFieldLanguage("name"),
        AdaaHelper.getFieldLanguage("name"),
        undefined,
        cb
      );

      /*
       * Sub gov-dif by gov-dir ID
       * */
    } else if (fieldName.includes("subGovernmentDirectionName")) {
      processDropdownRequest(
        subscriber,
        subGovDirectionApiService.getAllbyGovDirection(Constants.CONSTANT_WE_THE_UAE_2031_SUB_GOV_ID)
      );
    }
  };
};

/**
 * Populates the govDirection dropdown-list
 */
const govDirectionDropdownList = () => {
  const govDirectionApiService = inject(GovDirectionApiService);

  return (
    subscriber: Subscriber<ValueText[]>,
    { fieldName, cb }: { fieldName: string; cb: DataCallback<GovDirection[]> }
  ) => {
    /*
     * Gov-dirs by vision
     * */
    if (fieldName.includes("govDirectionName")) {
      processDropdownRequest<GovDirection>(
        subscriber,
        govDirectionApiService.getAll(),
        AdaaHelper.getFieldLanguage("name"),
        AdaaHelper.getFieldLanguage("name"),
        undefined,
        cb
      );

      /*
       * Auxillary gov-dirs
       * */
    } else if (fieldName.includes("auxDirectionName")) {
      processDropdownRequest(
        subscriber,
        govDirectionApiService.getAuxiliaryDirection(Constants.CONSTANT_WE_THE_UAE_2031_SUB_GOV_ID)
      );
    }
  };
};

/**
 * Populates the audit-trails dropdown-list
 */
const auditTrailDropdownList = () => {
  const auditTrailApiService = inject(AuditTrailApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName, configKey }: { fieldName: string; configKey: string }) => {
    const canGetObjectTypes =
      (fieldName === "objectTypeEN" ||
        fieldName === "objectTypeAE" ||
        fieldName === "itemTypeEN" ||
        fieldName === "itemTypeAE") &&
      (configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.AUDIT_TRAIL ||
        configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.USERS_PROFILE_OWNED_OBJECT);

    /*
     * Audit trail object types
     * */
    if (canGetObjectTypes) {
      processDropdownRequest(subscriber, auditTrailApiService.getObjectTypes());

      /*
       * Audit trail action types
       * */
    } else if (fieldName.includes("actionName")) {
      processDropdownRequest(subscriber, auditTrailApiService.getActionTypes());
    }
  };
};

/**
 * Populates the itemTypes dropdown-list
 */
const itemTypesDropdownList = () => {
  const propertiesService = inject(PropertiesService);
  const untilDestroyed = AdaaHelper.untilDestroyed();

  // Share the api response to avoid duplicate api calls
  const source$ = propertiesService.getAllItemTypes().pipe(
    untilDestroyed(),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    /*
     * Linked KPIs
     * */
    if (fieldName.includes("linkedKpiType")) {
      source$.subscribe({
        next: (response) => {
          if (response.inError) return;
          subscriber.next(
            AdaaHelper.setDropdownArray(
              response.responseData.filter(({ id }) => id === Constants.KPI_TYPE.NKPI || id === Constants.KPI_TYPE.UAE),
              AdaaHelper.getFieldLanguage("name"),
              AdaaHelper.getFieldLanguage("name")
            )
          );
        },
      });

      /*
       * Filtered item types
       * */
    } else if (fieldName.includes("itemName")) {
      source$.subscribe({
        next: (response) => {
          if (response.inError) return;

          subscriber.next(
            AdaaHelper.setDropdownArray(
              response.responseData.filter(
                ({ id, propType }) =>
                  propType !== Constants.CONSTANT_SERVICETYPES_TYPE || id !== Constants.CONSTANT_SUBGOVDIRECTION
              ),
              AdaaHelper.getFieldLanguage("name"),
              AdaaHelper.getFieldLanguage("name")
            )
          );
        },
      });

      /*
       * Data Entry Page
       * */
    } else if (fieldName.includes("kpiType")) {
      source$.subscribe({
        next: (response) => {
          if (response.inError) return;

          subscriber.next(
            AdaaHelper.setDropdownArray(
              response.responseData.filter(({ propType }) => propType === Constants.CONSTANT_KPITYPE_PROP),
              AdaaHelper.getFieldLanguage("name"),
              AdaaHelper.getFieldLanguage("name")
            )
          );
        },
      });

      /*
       * Main Outcome Type
       * */
    } else if (fieldName.includes("mainOutcomeTypeName")) {
      source$.subscribe({
        next: (response) => {
          if (response.inError) return;

          subscriber.next(
            AdaaHelper.setDropdownArray(
              response.responseData.filter(({ propType }) => propType === Constants.CONSTANT_MAIN_OUTCOME_TYPE_PROP),
              AdaaHelper.getFieldLanguage("name"),
              AdaaHelper.getFieldLanguage("name")
            )
          );
        },
      });
    }
  };
};

/**
 * Populates the User Groups dropdown-list
 */
const userGroupsDropdownList = () => {
  const userGroupsApiService = inject(UserGroupsApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    //Notification Group
    if (fieldName.includes("notificationGroup")) {
      processDropdownRequest(
        subscriber,
        userGroupsApiService.getallWithoutPagination(),
        "id",
        AdaaHelper.getFieldLanguage("name")
      );
    }
  };
};

/**
 * Populates the Notification Category dropdown-list
 */
const notificationCategoryDropdownList = () => {
  const notificationsApiService = inject(NotificationsApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName, configKey }: { fieldName: string; configKey: string }) => {
    const isNotificationPage =
      configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.NOTIFICATION_CONFIGURATION_ENTITY ||
      configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.NOTIFICATION_CONFIGURATION_PMO;

    //Notification Category
    if (fieldName.includes("categoryName") && isNotificationPage) {
      processDropdownRequest(subscriber, notificationsApiService.getCategories());
    }
  };
};

/**
 * Populates the TP Cycles dropdown-list
 */
const tpCyclesDropdownList = () => {
  const tpCyclesApiService = inject(TpCyclesApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    //TP Cycles
    if (fieldName.includes("nationalProjectCycleName")) {
      processDropdownRequest(subscriber, tpCyclesApiService.getAllCycles());
    }
  };
};

/**
 * Populates the Main Services dropdown-list
 */
const mainServicesDropdownList = () => {
  const serviceApiService = inject(ServicesApiService);

  return (
    subscriber: Subscriber<ValueText[]>,
    { fieldName, cb }: { fieldName: string; cb: DataCallback<ServiceModel[]> }
  ) => {
    if (fieldName.includes("mainServiceName")) {
      processDropdownRequest<ServiceModel>(
        subscriber,
        serviceApiService.getServicesByMap(),
        AdaaHelper.getFieldLanguage("name"),
        AdaaHelper.getFieldLanguage("name"),
        undefined,
        cb
      );
    }
  };
};

/**
 * Populates the Aux/Var Services dropdown-list
 */
const auxVarServicesDropdownList = () => {
  const serviceApiService = inject(ServicesApiService);

  return (
    subscriber: Subscriber<ValueText[]>,
    { fieldName, cb }: { fieldName: string; cb: DataCallback<ServiceModel[]> }
  ) => {
    //TP Cycles
    if (fieldName.includes("auxVarServiceName")) {
      processDropdownRequest<ServiceModel>(
        subscriber,
        serviceApiService.getSubServicesByMap(),
        AdaaHelper.getFieldLanguage("name"),
        AdaaHelper.getFieldLanguage("name"),
        undefined,
        cb
      );
    }
  };
};

/**
 * Populates the published/created by tp agreement dropdown-list
 */
const tpAgreementUsersDropdownList = () => {
  const tpApiService = inject(TpApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    if (fieldName.includes("createUserName") || fieldName.includes("publishUserName")) {
      processDropdownRequest(subscriber, tpApiService.getAgreementFilterUsers());
    }
  };
};

/**
 * Populates the pillar type dropdown-list
 */
const pillarTypeDropdownList = () => {
  const govScoresApiService = inject(GovScoresApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    if (fieldName.includes("pillarTypeName")) {
      processDropdownRequest(subscriber, govScoresApiService.getAvailablePillarTypes());
    }
  };
};

/**
 * AGM cycle list dropdown-list
 */
const agmCyclesDropdownList = () => {
  const agmCycleApiService = inject(AgmCycleApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    if (fieldName.includes("annualMeetingProjectCycleName")) {
      processDropdownRequest(subscriber, agmCycleApiService.getCycles());
    }
  };
};

/**
 * Assigned To HelpDesk list dropdown-list
 */
const userGroupsHelpDesk = () => {
  const userGroupsApiService = inject(UserGroupsApiService);
  return (subscriber: Subscriber<ValueText[]>, { fieldName, entityId }: { fieldName: string; entityId?: number }) => {
    if (fieldName.includes("assigned")) {
      userGroupsApiService.getallHelpdesk().subscribe({
        next: (response) => {
          if (response.inError) return;

          const filteredData = entityId
            ? response.responseData
                .filter((item) => item.entityId === entityId)
                .map((e) => ({
                  ...e,
                  nameEN: e.nameEN.split(" - ")[1],
                  nameAE: e.nameAE.split(" - ")[1],
                }))
            : [];

          subscriber.next(
            AdaaHelper.setDropdownArray(
              filteredData,
              AdaaHelper.getFieldLanguage("name"),
              AdaaHelper.getFieldLanguage("name")
            )
          );
        },
      });
    }
  };
};

const countriesDropdownList = () => {
  const intpCountriesApiService = inject(IntpCountriesApiService);

  return (
    subscriber: Subscriber<ValueText[]>,
    { fieldName, configKey, cb }: { fieldName: string; configKey: string; cb: DataCallback<IntpCountryModel[]> }
  ) => {
    if (fieldName.includes("countryName") || fieldName.includes("hostedCountryName")) {
      intpCountriesApiService.getCountries().subscribe({
        next: (response) => {
          if (response.inError) return;

          if (configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.INTP_PROJECTS_CONF_LIST)
            response.responseData = response.responseData.filter((e) => e.id !== Constants.CONSTANT_UAE_COUNTRY_ID);

          cb(response.responseData);

          subscriber.next(
            AdaaHelper.setDropdownArray(
              response.responseData,
              AdaaHelper.getFieldLanguage("name"),
              AdaaHelper.getFieldLanguage("name")
            )
          );
        },
      });
    }
  };
};

// INTP Milestone -> Projects DDL
const intpProjectDropdownList = () => {
  const intpProjectsApiService = inject(IntpProjectsApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName }: { fieldName: string }) => {
    if (fieldName === "internationalProjectNameAE" || fieldName === "internationalProjectNameEN") {
      processDropdownRequest(subscriber, intpProjectsApiService.getAllProjects(AdaaHelper.entity.id));
    }
  };
};

const ownersDropdownList = () => {
  const usersApiService = inject(UsersApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName, configKey }: { fieldName: string; configKey: string }) => {
    if (
      (fieldName === "ownerNameAE" || fieldName === "ownerNameEN") &&
      configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.DATA_ACTIVITY_CONF_LIST
    ) {
      processDropdownRequest(subscriber, usersApiService.getOwnersPerEntity(AdaaHelper.entity.id));
    }
  };
};

const tracksDropdownList = () => {
  const govProjectsApiService = inject(GovProjectsApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName, configKey }: { fieldName: string; configKey: string }) => {
    const configKeys = [
      Constants.MAIN_TABLE_LIST_CONF_KEY.AIKPIS,
      Constants.MAIN_TABLE_LIST_CONF_KEY.AI_PROJECT_MILESTONE_LIST,
      Constants.MAIN_TABLE_LIST_CONF_KEY.AI_PROJECT_TASK_LIST,
    ];

    if ((fieldName === "trackNameAE" || fieldName === "trackNameEN") && configKeys.includes(configKey)) {
      processDropdownRequest(subscriber, govProjectsApiService.getTracks("ai-project"));
    }
  };
};

const milestonesDropdownList = () => {
  const govProjectsApiService = inject(GovProjectsApiService);

  return (subscriber: Subscriber<ValueText[]>, { fieldName, configKey }: { fieldName: string; configKey: string }) => {
    if (configKey !== Constants.MAIN_TABLE_LIST_CONF_KEY.AI_PROJECT_TASK_LIST) return;
    if (fieldName !== "milestoneNameAE" && fieldName !== "milestoneNameEN") return;

    processDistinctDropdownRequest(
      subscriber,
      govProjectsApiService.getAllMilestones("ai-project"),
      AdaaHelper.getFieldLanguage("name") as keyof GovProjectMilestone
    );
  };
};

export const getDropdownListOptions = () => ({
  entities: entitiesDropdownList(),
  orgUnit: orgUnitDropdownList(),
  subGovDir: subGovDirectionDropdownList(),
  govDirs: govDirectionDropdownList(),
  cycles: cyclesDropdownList(),
  itemTypes: itemTypesDropdownList(),
  auditTrail: auditTrailDropdownList(),
  otherEntities: otherEntitiesDropdownList(),
  tp: tpDropdownList(),
  objectives: objectivesDropdownList(),
  userGroups: userGroupsDropdownList(),
  notificationCategories: notificationCategoryDropdownList(),
  tpCycles: tpCyclesDropdownList(),
  mainService: mainServicesDropdownList(),
  auxVarService: auxVarServicesDropdownList(),
  tpAgreementUsers: tpAgreementUsersDropdownList(),
  pillarTypes: pillarTypeDropdownList(),
  agmProjectCycles: agmCyclesDropdownList(),
  helpDesk: userGroupsHelpDesk(),
  pillar: pillarsDropdownList(),
  countries: countriesDropdownList(),
  intp: intpProjectDropdownList(),
  owners: ownersDropdownList(),
  tracks: tracksDropdownList(),
  milestones: milestonesDropdownList(),
});
